import { loadStripe } from "@stripe/stripe-js";

// Public publishable keys, safe to commit. Stripe publishable keys (pk_...) are
// designed to ship in browser bundles, which they do anyway on every page
// that initialises Stripe. Matching secret keys (sk_live_/sk_test_) live as
// STRIPE_SECRET_KEY on the per-environment Supabase edge-function secrets —
// never commit those.
//
// We hostname-switch instead of relying on build-time env vars because
// Lovable doesn't inject VITE_* on this project. Production (lembra.com.au)
// resolves to pk_live_; everywhere else (localhost, lovable previews,
// future staging frontends) resolves to pk_test_. .env.local can still
// force a specific key when needed.
const PK_LIVE =
  "pk_live_51TMQkLK0ELKer7ldlszkvLikI9JdqWN9YszM0jLiVBMrs0cQA3vMVwqdHeu6PIlN6AZ3tkdZ1SYZmJBXBurbt25q00H7v6rFra";
const PK_TEST =
  "pk_test_51TMQkXK9K9pIaefG70GVEDklHfp2mwWE9LiB4qA0wB31E7iCLkH6o0HK9vD3NZceNuTpUZ2XOpgBmzkM2GnEExxO00pZddVGPN";

const PROD_HOSTS = new Set(["lembra.com.au", "www.lembra.com.au"]);

function inferPublishableKey(): string {
  if (typeof window === "undefined") return PK_TEST;
  return PROD_HOSTS.has(window.location.hostname) ? PK_LIVE : PK_TEST;
}

const STRIPE_PUBLISHABLE_KEY =
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || inferPublishableKey();

/** Singleton Stripe.js instance (lazy-loaded on first use) */
export const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

/** Redirect to Stripe Checkout via our Edge Function */
export async function redirectToCheckout(params: {
  supabaseFunctionsUrl: string;
  orderData: Record<string, unknown>;
  authToken?: string;
}): Promise<{ error?: string }> {
  const { supabaseFunctionsUrl, orderData, authToken } = params;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const res = await fetch(`${supabaseFunctionsUrl}/create-checkout-session`, {
    method: "POST",
    headers,
    body: JSON.stringify(orderData),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { error: data.error || `Payment failed (${res.status})` };
  }

  const { sessionUrl } = await res.json();
  if (sessionUrl) {
    window.location.href = sessionUrl;
    return {};
  }

  return { error: "No checkout URL returned" };
}
